<template>
    <div
        class="organisation-header d-flex flex-column align-center ga-3 no-print"
    >
        <template v-if="loading || paymentsStore.loadingPaymentMethods">
            <div class="d-flex align-center flex-column ga-10 w-100">
                <v-skeleton-loader type="chip" class="w-100" />
                <v-skeleton-loader type="subtitle" class="w-100" />
                <v-skeleton-loader type="text" class="w-100" />
            </div>
        </template>

        <template v-else>
            <img
                v-if="showLogo"
                loading="eager"
                :class="[
                    'organisation-header__logo',
                    {
                        'organisation-header__logo--large':
                            !showPaymentBreakdown,
                        'organisation-header__logo--hide-mobile':
                            showPaymentBreakdown && xs,
                    },
                ]"
                :src="`https://imagedelivery.net/cdn-cgi/image/width=340/C3ccYXB38iKjIvgGGu5HTg/${logoShortcode}/public`"
                :alt="`${logoShortcode} logo`"
                data-cy="organisation-logo"
                width="70"
                height="70"
                @error="onLogoError"
            />
            <div class="organisation-header__info">
                <div class="organisation-header__name dark-text">
                    {{ organisationName }}
                </div>
                <div class="organisation-header__form-type">
                    {{ paymentTitle }}
                </div>
            </div>

            <Transition name="slide-fade">
                <organisation-amount-info
                    v-if="showPaymentBreakdown"
                    :default-local-amount="amountDetails.localAmount"
                    :default-local-currency="amountDetails.localCurrency"
                    :default-base-amount="amountDetails.baseAmount"
                    :default-base-currency="amountDetails.baseCurrency"
                    :language="String(sessionStore.language)"
                    :countryCode="String(sessionStore.countryCode)"
                />
            </Transition>

            <Transition name="slide-fade">
                <organisationPaymentBreakdown
                    v-if="
                        showPaymentBreakdown &&
                        (sessionStore.emailAddress ||
                            supplementaryBreakdown.length > 0)
                    "
                    :email-address="sessionStore.emailAddress"
                    :supplementary-breakdown="supplementaryBreakdown"
                />
            </Transition>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";
import { useRoute } from "vue-router";

import {
    useSessionStore,
    useCheckoutStore,
    usePaymentsStore,
    useOrganisationStore,
} from "@/plugins/store";
import { routeNames } from "@/constants/routeNames";
import { getSupplementaryDataLabel } from "@/helpers/paymentStatusHelpers";
import organisationAmountInfo from "./organisationAmountInfo.vue";
import organisationPaymentBreakdown from "./organisationPaymentBreakdown.vue";

defineProps<{
    logoShortcode: string;
    organisationName: string | undefined;
    paymentTitle: string;
    showLogo: boolean;
    loading: boolean;
    onLogoError: () => void;
}>();

const { xs } = useDisplay();
const sessionStore = useSessionStore();
const organisationStore = useOrganisationStore();
const checkoutStore = useCheckoutStore();
const paymentsStore = usePaymentsStore();
const route = useRoute();

/**
 */
const amountDetails = computed(() => {
    return {
        localAmount: checkoutStore.supplementaryData?.amount || "0",
        localCurrency:
            organisationStore.organisation?.baseCurrencyCode || "GBP",
        baseAmount: checkoutStore.supplementaryData?.amount || "0",
        baseCurrency: organisationStore.organisation?.baseCurrencyCode || "GBP",
    };
});

/**
 * Breakdown of supplementary form data to display in the organisation header. Excludes amount and
 * email address fields, as they are displayed separately.
 * Loops through the supplementary data and finds the corresponding field in the form structure
 * to get the display name. If no display name is found, it formats the key name to a more readable format.
 */
const supplementaryBreakdown = computed(() => {
    const data = checkoutStore.supplementaryData;
    if (!data) return [];

    return Object.entries(data)
        .filter(([key]) => key !== "amount" && key !== "emailAddress")
        .map(([key, value]) => {
            const label =
                getSupplementaryDataLabel(key) ||
                key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (s) => s.toUpperCase());
            const formatted = formatValue(value);
            return { label, value: formatted };
        })
        .filter(({ value }) => value !== "");
});

/**
 * Formats a value for display in the supplementary breakdown. If the value is an array,
 * it joins the values with commas. If the value is an object, it joins the values
 * of the object with commas. If the value is null or undefined, it returns an
 * empty string. Otherwise, it returns the value as a string.
 */
function formatValue(value: unknown): string {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return value.map(formatValue).join(", ");
    if (typeof value === "object") {
        return Object.values(value as Record<string, unknown>)
            .filter((v) => v !== null && v !== undefined && v !== "")
            .map(formatValue)
            .join(", ");
    }
    return String(value);
}

/**
 * Determines if the payment breakdown section should be displayed based on
 * the current route.
 */
const showPaymentBreakdown = computed(() => {
    const routeName = route.name as routeNames;
    return ![
        routeNames.INVOICE,
        routeNames.SUPPLEMENTARY_FORM,
        routeNames.FORM,
        routeNames.PAYMENT_STATUS,
        routeNames.E_MANDATES_LANDING_PAGE,
        routeNames.E_MANDATES_FORM,
        routeNames.E_MANDATES_REVIEW_DETAILS,
        routeNames.E_MANDATES_CONFIRMATION,
        routeNames.AUTH_REDIRECT,
        routeNames.AUTH_SIGN_OUT,
        routeNames.PAYMENT_LINK_UNAVAILABLE,
    ].includes(routeName);
});
</script>

<style scoped>
.slide-fade-enter-active {
    transition:
        opacity 0.35s ease,
        transform 0.35s ease;
}

.slide-fade-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.organisation-header {
    margin-bottom: 8px;
    border-radius: var(--radius-surface);
    padding: 16px;
    background-color: var(--white);
    border: 1px solid var(--surface-disabled);
    width: 100%;
    max-width: 400px;
    min-width: 280px;
    overflow: hidden;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.organisation-header__logo {
    width: 150px;
    height: auto;
    min-height: 50px;
    object-fit: contain;
    border-radius: var(--radius-container);
    padding: 4px;
    flex-shrink: 0;
    transition:
        width 0.35s ease,
        height 0.35s ease;
}

.organisation-header__logo--large {
    width: 340px;
    height: 200px;
}

.organisation-header__logo--hide-mobile {
    display: none;
}

.organisation-header__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: center;
    text-align: center;
}

.organisation-header__name {
    font-weight: 600;
    font-size: 18px;
}

.organisation-header__form-type {
    font-size: 12px;
    color: var(--text-secondary);
    text-transform: capitalize;
}

@media (max-width: 839px) {
    .organisation-header {
        max-width: 100%;
        min-width: 100%;
        gap: 8px;
    }

    .organisation-header__logo {
        width: 100px;
        height: auto;
        min-height: 70px;
    }

    .organisation-header__logo--large {
        width: 60%;
        height: 140px;
    }

    .organisation-header__name {
        font-size: 16px;
    }
}
</style>
